import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/shared/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";

export default function Offices() {
  const [offices, setOffices] = useState([]);
  const [adding, setAdding] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [removing, setRemoving] = useState(null);

  const load = () => api.get("/offices").then((r) => setOffices(r.data)).catch(() => setOffices([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!adding.trim()) return;
    try { const r = await api.post("/offices", { name: adding.trim() }); setOffices(r.data); setAdding(""); toast.success("Office added."); }
    catch (e) { toast.error(e.message); }
  };
  const rename = async (old) => {
    if (!editValue.trim()) { setEditing(null); return; }
    try { const r = await api.patch("/offices", { old, new: editValue.trim() }); setOffices(r.data); setEditing(null); toast.success("Office renamed."); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async () => {
    try { const r = await api.del(`/offices?name=${encodeURIComponent(removing)}`); setOffices(r.data); toast.success("Office removed."); }
    catch (e) { toast.error(e.message); }
    setRemoving(null);
  };

  return (
    <Card className="p-5" data-testid="offices-panel">
      <SectionTitle className="mb-1">Offices</SectionTitle>
      <p className="text-[13px] text-nb-ink2 mb-4">
        The list of office locations used across Teams, Members and when inviting people. Renaming an office updates everyone assigned to it.
      </p>

      <div className="flex items-center gap-2 mb-5 max-w-md">
        <Input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add an office, e.g. Bristol" className="h-10" data-testid="office-add-input" />
        <Button onClick={add} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2 shrink-0" data-testid="office-add-btn"><Plus size={16} /> Add</Button>
      </div>

      <div className="space-y-1.5 max-w-md">
        {offices.length === 0 && <p className="text-[14px] text-nb-ink2">No offices yet.</p>}
        {offices.map((o) => (
          <div key={o} className="flex items-center gap-2 px-3 py-2 rounded-[10px]" style={{ border: "1px solid var(--border-subtle)" }} data-testid={`office-row-${o}`}>
            {editing === o ? (
              <>
                <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(o)} className="h-9 flex-1" autoFocus data-testid="office-edit-input" />
                <button onClick={() => rename(o)} className="p-1.5 text-nb-ink2 hover:text-nb-ink" data-testid="office-edit-save"><Check size={16} /></button>
                <button onClick={() => setEditing(null)} className="p-1.5 text-nb-ink2 hover:text-nb-ink"><X size={16} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 text-[14px] text-nb-ink">{o}</span>
                <button onClick={() => { setEditing(o); setEditValue(o); }} className="p-1.5 text-nb-ink2 hover:text-nb-ink" title="Rename" data-testid={`office-rename-${o}`}><Pencil size={15} /></button>
                <button onClick={() => setRemoving(o)} className="p-1.5 text-nb-ink2 hover:text-[var(--neg-text)]" title="Remove" data-testid={`office-remove-${o}`}><Trash2 size={15} /></button>
              </>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing}?`}
        description="People assigned to this office will have their office cleared. This cannot be undone."
        confirmLabel="Remove office" onConfirm={remove}
      />
    </Card>
  );
}
